import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  Avatar,
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { Season } from '../../../../../../corelogic/domain/models/season.model';
import { TournamentParticipant } from '../../../../../../corelogic/domain/models/tournament.model';
import { CODE_TO_COUNTRY } from '../../../../../../corelogic/domain/utils/country.utils';
import { formatDateShort } from '../../../../../../corelogic/domain/utils/date.utils';
import { getEnrichedSeasonResults } from '../../../../../../corelogic/domain/utils/tournament.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import RankIcon from '../../Shared/GetRankIcon';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    height: '100%',
    overflowY: 'hidden',
    overflowX: 'hidden',
    pt: 0,
  },
  seasonToggleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    height: '40px',
    minHeight: '40px',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    pb: 0,
    overflow: 'visible',
    flexShrink: 0,
  },
  toggleButton: {
    color: '#cccccc',
    borderColor: 'transparent',
    padding: '0 15px',
    height: '100%',
    minWidth: '140px',
    justifyContent: 'flex-start',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      color: '#ffffff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(50, 50, 50, 0.7)',
      },
    },
  },
  toggleButtonWrapper: {
    display: 'flex',
    flex: '1 0 auto',
    mr: 2,
    minWidth: 0,
    maxWidth: 'calc(100% - 200px)',
    overflowX: 'auto',
    flexGrow: 1,
    marginRight: 2,
    height: '100%',
    scrollBehavior: 'smooth',
    msOverflowStyle: 'none', // Hide scrollbar in IE/Edge
    scrollbarWidth: 'none', // Hide scrollbar in Firefox
    '&::-webkit-scrollbar': {
      display: 'none', // Hide scrollbar in Chrome/Safari
    },
    //scrollbarWidth: 'thin',
    //'&::-webkit-scrollbar': {
    //  height: '4px',
    //  width: '4px',
    //},
    //'&::-webkit-scrollbar-track': {
    //  background: 'rgba(255, 255, 255, 0.1)',
    //  borderRadius: '2px',
    //},
    //'&::-webkit-scrollbar-thumb': {
    //  background: 'rgba(255, 255, 255, 0.3)',
    //  borderRadius: '2px',
    //  '&:hover': {
    //    background: 'rgba(255, 255, 255, 0.5)',
    //  },
    //},
  },
  toggleButtonGroup: {
    bgcolor: 'transparent',
    width: 'max-content',
    height: '100%',
    display: 'flex',
    '& .MuiToggleButtonGroup-grouped': {
      textTransform: 'none',
      fontFamily: 'Arial',
      fontSize: 'clamp(0.8rem, 1vw, 0.9rem)',
      margin: '0',
      height: '100%',
      position: 'relative',
    },
    '& .Mui-selected': {
      borderBottom: '2px solid #cc0000',
    },
  },
  scrollArrow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(30, 30, 30, 0.9)',
    color: '#cccccc',
    cursor: 'pointer',
    position: 'absolute',
    zIndex: 20,
    boxShadow: '0 0 5px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(50, 50, 50, 0.9)',
      color: '#ffffff',
    },
    '&.left-arrow': {
      left: 0,
    },
    '&.right-arrow': {
      right: 'calc(200px + 8px)',
    },
  },
  seasonInfoDisplay: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: '200px',
    pl: 1.5,
    pr: 1.5,
    gap: 1,
    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
    flexShrink: 0,
    position: 'sticky',
    right: 0,
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    zIndex: 2,
  },
  seasonName: {
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    gap: 1,
    position: 'relative',
    flex: 1,
  },
  seasonNumber: {
    fontWeight: 'bold',
    marginRight: '6px',
    color: '#cc0000',
  },
  seasonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    bgcolor: 'rgba(20, 20, 20, 0.95)',
    borderRadius: '8px 8px 0 0',
    border: '1px solid rgba(30, 30, 30, 0.8)',
    borderBottom: 'none',
    p: 2,
    mb: -0.5,
  },
  seasonTitle: {
    fontFamily: 'Bebas Neue',
    fontSize: 'clamp(1.1rem, 1.3vw, 1.3rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  seasonStatus: (isActive: boolean, isUpcoming: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    width: '40px',
    height: '27px',
    textAlign: 'center',
    gap: 0.5,
    borderRadius: '4px',
    fontSize: '0.5rem',
    justifyContent: 'center',
    bgcolor: isUpcoming
      ? 'rgba(255, 152, 0, 0.2)'
      : isActive
        ? 'rgba(0, 128, 0, 0.2)'
        : 'rgba(128, 128, 128, 0.2)',
    border: isUpcoming
      ? '1px solid rgba(255, 152, 0, 0.5)'
      : isActive
        ? '1px solid rgba(0, 128, 0, 0.5)'
        : '1px solid rgba(128, 128, 128, 0.5)',
    color: isUpcoming ? '#ffb74d' : isActive ? '#4caf50' : '#aaaaaa',
  }),
  seasonDate: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    color: '#cccccc',
    fontSize: '0.5rem',
  },
  tableContainer: {
    height: 'calc(100% - 40px)',
    minHeight: '300px',
    bgcolor: 'transparent',
    backgroundImage:
      'linear-gradient(to bottom, rgba(10, 10, 10, 0.9), rgba(0, 0, 0, 0.95))',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
    border: '1px solid rgba(30, 30, 30, 0.8)',
    overflow: 'auto',
    flexGrow: 1,
  },
  tableHead: {
    '& .MuiTableCell-head': {
      bgcolor: 'rgba(0, 0, 0, 0.9)',
      color: '#ffffff',
      fontFamily: 'Rubik Mono One',
      fontSize: 'clamp(0.7rem, 1vw, 0.8rem)',
      borderBottom: '2px solid rgba(140, 0, 0, 0.7)',
      padding: '8px 12px',
      letterSpacing: '0.5px',
    },
  },
  tableRow: (isCurrentUser: boolean) => ({
    bgcolor: isCurrentUser ? 'rgba(100, 0, 0, 0.2)' : 'transparent',
    '&:nth-of-type(odd)': {
      bgcolor: isCurrentUser
        ? 'rgba(100, 0, 0, 0.25)'
        : 'rgba(20, 20, 20, 0.5)',
    },
    '&:hover': {
      bgcolor: isCurrentUser ? 'rgba(100, 0, 0, 0.3)' : 'rgba(30, 30, 30, 0.7)',
    },
    transition: 'background-color 0.2s',
    height: '54px',
  }),
  tableCell: {
    color: '#e0e0e0',
    borderBottom: '1px solid rgba(40, 40, 40, 0.8)',
    padding: '6px 12px',
  },
  playerCell: {
    display: 'flex',
    alignItems: 'left',
    gap: 1.5,
    justifyContent: 'space-between',
  },
  playerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexGrow: 1,
    paddingLeft: '6px',
    justifyContent: 'space-between',
  },
  playerName: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontFamily: 'Bebas Neue, sans-serif',
    letterSpacing: '0.5px',
    fontSize: 'clamp(0.85rem, 1vw, 0.95rem)',
  },
  flag: {
    width: '20px',
    height: '14px',
    borderRadius: '2px',
    display: 'block',
    objectFit: 'cover',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
  },
  nationality: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointsCell: {
    color: '#ffffff',
    fontWeight: 'bold',
    fontSize: 'clamp(0.8rem, 1.2vw, 0.9rem)',
    textShadow: '0 0 4px rgba(140, 0, 0, 0.6)',
  },
  winnerChip: {
    bgcolor: 'rgba(255, 215, 0, 0.2)',
    color: '#FFD700',
    border: '1px solid rgba(255, 215, 0, 0.5)',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    height: 20,
  },
  userIndicator: {
    color: '#900000',
    fontWeight: 'bold',
    fontSize: '0.65rem',
    bgcolor: 'rgba(140, 0, 0, 0.15)',
    border: '1px solid rgba(140, 0, 0, 0.3)',
    height: 20,
  },
  avatar: {
    width: 32,
    height: 32,
    border: '1px solid rgba(140, 0, 0, 0.5)',
    backgroundColor: 'rgba(20, 20, 20, 0.7)',
  },
  emptyState: {
    textAlign: 'center',
    padding: 4,
    color: '#cccccc',
    backgroundImage:
      'linear-gradient(to bottom, rgba(10, 10, 10, 0.9), rgba(0, 0, 0, 0.95))',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
    border: '1px solid rgba(30, 30, 30, 0.8)',
    height: '200px',
    minHeight: '180px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: 'rgba(204, 0, 0, 0.4)',
    marginBottom: 1,
  },
};

const SeasonResults = ({
  seasons,
  participants,
  username,
  isFMMAPublic,
}: {
  seasons: Season[];
  participants: TournamentParticipant[];
  username: string;
  isFMMAPublic: boolean;
}) => {
  // Scroll arrows
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Filter seasons with start dates (valid seasons)
  const validSeasons = seasons.filter((s) => s.startDate);

  // Default to first season if any are available
  const [selectedSeason, setSelectedSeason] = useState(
    validSeasons.length > 0 ? validSeasons[0].seasonId : '',
  );

  // Find current season object
  const selectedSeasonObject = validSeasons.find(
    (s) => s.seasonId === selectedSeason,
  );

  // Determine season status
  const today = new Date();
  const startDate = selectedSeasonObject?.startDate
    ? new Date(selectedSeasonObject.startDate)
    : null;
  const endDate = selectedSeasonObject?.endDate
    ? new Date(selectedSeasonObject.endDate)
    : null;

  const isUpcoming = startDate && startDate > today;
  const isActive = !isUpcoming && (!endDate || endDate > today);
  const isCompleted = !isUpcoming && !isActive;

  // Get the enriched season results from the utility function
  const enrichedSeasonResults = selectedSeason
    ? getEnrichedSeasonResults(
        participants,
        selectedSeason,
        username,
        validSeasons,
      )
    : [];

  // Make sure we use the selected season even if we change seasons in the component
  useEffect(() => {
    if (
      validSeasons.length > 0 &&
      !validSeasons.find((s) => s.seasonId === selectedSeason)
    ) {
      setSelectedSeason(validSeasons[0].seasonId);
    }
  }, [seasons]);

  const handleSeasonChange = (
    event: React.SyntheticEvent,
    newSeason: string,
  ) => {
    if (newSeason !== null) {
      setSelectedSeason(newSeason);
    }
  };

  // Check if scrolling is needed and update arrow visibility
  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Handle scroll event
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', checkScrollPosition);

      // Run initial check with a small delay to ensure layout is complete
      const initialCheckTimeout = setTimeout(() => {
        checkScrollPosition();
      }, 100);

      // Check on window resize
      window.addEventListener('resize', checkScrollPosition);

      return () => {
        if (scrollContainer) {
          scrollContainer.removeEventListener('scroll', checkScrollPosition);
        }
        window.removeEventListener('resize', checkScrollPosition);
        clearTimeout(initialCheckTimeout);
      };
    }
  }, []);

  // Recheck when selected season changes
  useEffect(() => {
    // This will re-evaluate arrow visibility after a season is selected
    const recalculateTimeout = setTimeout(() => {
      checkScrollPosition();
    }, 50);

    return () => clearTimeout(recalculateTimeout);
  }, [selectedSeason]);

  // Handle scroll buttons
  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // If no seasons, show empty state
  if (validSeasons.length === 0) {
    return (
      <Box sx={styles.emptyState}>
        <EmojiEventsIcon
          sx={{ fontSize: 40, color: '#cc0000', opacity: 0.7, mb: 2 }}
        />
        {isFMMAPublic ? (
          <>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Rubik Mono One', mb: 2 }}
            >
              This is a Global Annual Tournament
            </Typography>
            <Typography variant="body2">
              To view global season results, please view the relevant global
              season tournament
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant="h6"
              sx={{ fontFamily: 'Rubik Mono One', mb: 2 }}
            >
              No Seasons Available
            </Typography>
            <Typography variant="body2">
              Season results will appear here once seasons begin.
            </Typography>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Season toggle buttons */}
      <Box sx={styles.seasonToggleContainer}>
        {/* Left arrow */}
        {showLeftArrow && (
          <Box
            onClick={handleScrollLeft}
            sx={styles.scrollArrow}
            className="left-arrow"
          >
            <ArrowBackIosIcon fontSize="small" />
          </Box>
        )}

        {/* Toggle button group */}
        <Box ref={scrollContainerRef} sx={styles.toggleButtonWrapper}>
          <ToggleButtonGroup
            value={selectedSeason || ''}
            exclusive
            onChange={handleSeasonChange}
            aria-label="seasons"
            sx={styles.toggleButtonGroup}
          >
            {validSeasons.map((season, index) => (
              <ToggleButton
                key={season.seasonId}
                value={season.seasonId}
                sx={styles.toggleButton}
              >
                <Box sx={styles.seasonName}>
                  {/* season number */}
                  <Typography
                    component="span"
                    style={styles.seasonNumber as React.CSSProperties}
                  >
                    S{index + 1}
                  </Typography>
                  {season.seasonName || `Season ${index + 1}`}
                  {season.endDate && season.endDate > today && (
                    <Box component="span" sx={{ ml: 1 }}>
                      <PulsingActiveIcon />
                    </Box>
                  )}
                </Box>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* Right arrow */}
        {showRightArrow && (
          <Box
            onClick={handleScrollRight}
            sx={styles.scrollArrow}
            className="right-arrow"
          >
            <ArrowForwardIosIcon fontSize="small" />
          </Box>
        )}

        {/* Season info in right corner */}
        <Box sx={styles.seasonInfoDisplay}>
          {selectedSeasonObject && (
            <>
              <Box sx={styles.seasonStatus(!!isActive, !!isUpcoming)}>
                {isActive ? (
                  <>
                    <PulsingActiveIcon />
                    {/*<Typography component="span">Active</Typography>*/}
                  </>
                ) : isUpcoming ? (
                  <>
                    <HourglassEmptyIcon fontSize="small" />
                    {/*<Typography component="span">Upcoming</Typography>*/}
                  </>
                ) : (
                  <>
                    <CheckCircleIcon fontSize="small" />
                    {/*<Typography component="span">Completed</Typography>*/}
                  </>
                )}
              </Box>
              <Box sx={styles.seasonDate}>
                <CalendarMonthIcon fontSize="small" />
                <Typography component="span">
                  {selectedSeasonObject.startDate &&
                  selectedSeasonObject.endDate
                    ? `${formatDateShort(selectedSeasonObject.startDate)} - ${formatDateShort(selectedSeasonObject.endDate)}`
                    : 'Dates not set'}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Season content */}
      {selectedSeasonObject && (
        <TableContainer component={Paper} sx={styles.tableContainer}>
          <Table stickyHeader>
            <TableHead sx={styles.tableHead}>
              <TableRow>
                <TableCell width="10%" align="center">
                  RANK
                </TableCell>
                <TableCell width="30%" align="left" sx={{ pl: 2 }}>
                  PLAYER
                </TableCell>
                <TableCell width="20%" align="center">
                  NATION
                </TableCell>
                <TableCell width="20%" align="center">
                  FIGHTS
                </TableCell>
                <TableCell width="20%" align="right">
                  POINTS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrichedSeasonResults.length > 0 ? (
                enrichedSeasonResults.map((participant, index) => {
                  const isCurrentUser = participant.username === username;
                  // Only show winner indicator if season is completed and they're ranked #1
                  const isWinner = isCompleted && index === 0;
                  const participatedFights =
                    participant.participatedFights || 0;
                  const seasonPoints = participant.seasonPoints || 0;

                  return (
                    <TableRow
                      key={participant.username}
                      sx={styles.tableRow(isCurrentUser)}
                    >
                      <TableCell sx={styles.tableCell} align="center">
                        <RankIcon rank={index} />
                      </TableCell>
                      <TableCell sx={styles.tableCell}>
                        <Box sx={styles.playerCell}>
                          <Avatar
                            src={participant.profilePicture}
                            alt={participant.username}
                            sx={styles.avatar}
                          >
                            <SafeText
                              text={participant.username
                                ?.charAt(0)
                                .toUpperCase()}
                              fallback={SECURITY_FALLBACKS.USER_INITIAL}
                            />
                          </Avatar>

                          <Box sx={styles.playerInfo}>
                            <Box sx={styles.playerName}>
                              <SafeText
                                text={participant.username}
                                fallback={SECURITY_FALLBACKS.USERNAME}
                              />
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  marginLeft: 'auto',
                                }}
                              >
                                {isCurrentUser && (
                                  <Chip
                                    size="small"
                                    label="YOU"
                                    sx={styles.userIndicator}
                                  />
                                )}
                                {isWinner && (
                                  <Chip
                                    size="small"
                                    label="WINNER"
                                    sx={styles.winnerChip}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.tableCell} align="center">
                        <Box sx={styles.nationality}>
                          {participant.nationality && (
                            <Tooltip
                              title={
                                CODE_TO_COUNTRY[participant.nationality] ||
                                participant.nationality
                              }
                            >
                              <Box>
                                <ReactCountryFlag
                                  countryCode={participant.nationality}
                                  svg
                                  style={styles.flag as React.CSSProperties}
                                  title={
                                    CODE_TO_COUNTRY[participant.nationality] ||
                                    participant.nationality
                                  }
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={styles.tableCell} align="center">
                        {participatedFights} / {8}
                      </TableCell>
                      <TableCell
                        sx={{ ...styles.tableCell, ...styles.pointsCell }}
                        align="right"
                      >
                        {seasonPoints}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={styles.emptyState}>
                      <EmojiEventsIcon sx={styles.emptyStateIcon} />
                      <Typography
                        sx={{ color: '#aaaaaa', textAlign: 'center' }}
                      >
                        {isUpcoming
                          ? "This season hasn't started yet. Check back when it begins!"
                          : 'No participants have joined this season yet.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default SeasonResults;
